import type { StructuralFinding } from '@shared/client-uploads';
import { Badge } from '@/components/ui/badge';

export interface ClientUploadReviewMetadata {
  severity: 'amber' | 'red';
  projectId: string;
  pulseId: number | null;
  sessionId: string;
  fileId: string;
  fileName: string;
  reviewVersion: number;
  findings: StructuralFinding[];
  explanation: string;
}

export function ClientUploadReviewBadge({ severity }: { severity: unknown }) {
  return <Badge className={severity === 'red'
    ? 'border-red-700 bg-red-900 text-red-100 text-xs'
    : 'border-amber-800/60 bg-amber-950/40 text-amber-200 text-xs'}>
    {severity === 'red' ? 'Critical file review' : 'File review required'}
  </Badge>;
}

export function ClientUploadNotificationDetails({ metadata }: { metadata: Partial<ClientUploadReviewMetadata> }) {
  const findings = Array.isArray(metadata.findings) ? metadata.findings : [];
  return <details
    className="mt-2 rounded-md border border-gray-600 bg-gray-900/40 p-2 text-xs text-gray-200"
    data-testid="client-upload-review-details"
    onClick={event => event.stopPropagation()}
    onPointerDown={event => event.stopPropagation()}
    onKeyDown={event => event.stopPropagation()}
  >
    <summary className="cursor-pointer text-gray-300">Review findings and context</summary>
    <div className="mt-2 space-y-2">
      <p className="break-words font-medium">{metadata.fileName || 'Unnamed file'}</p>
      <p className="break-words">{metadata.explanation || 'Inspection information is unavailable. Review the current file before approval.'}</p>
      {metadata.severity === 'red' && <p className="text-red-200">Executable content or a format mismatch can pose a critical risk when the original is opened.</p>}
      <ul className="space-y-1">
        {findings.map((finding, index) => <li key={`${finding.code}-${index}`} className={`break-words border-l-2 pl-2 ${finding.severity === 'red' ? 'border-red-500 text-red-200' : finding.severity === 'amber' ? 'border-amber-700 text-amber-200' : 'border-sky-700 text-sky-200'}`}>
          <span className="font-mono">{finding.code}</span>: {finding.detail}
        </li>)}
      </ul>
      <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-2 gap-y-1">
        {([
          ['Project', metadata.projectId], ['Pulse', metadata.pulseId],
          ['Submission', metadata.sessionId], ['File', metadata.fileId],
          ['Review version', metadata.reviewVersion],
        ] as const).map(([label, value]) => value != null && <div className="contents" key={label}>
          <dt className="text-gray-400">{label}</dt><dd className="break-all">{String(value)}</dd>
        </div>)}
      </dl>
      <p className="text-gray-400">This notification records a review snapshot. Use the current file review to approve or reject.</p>
    </div>
  </details>;
}
